import type { VisaManualOverride } from "@/types/visa-manual-override"

/** Admin visa_metadata goal values (form + DB `goal_type`). */
export type GoalType = "relocation" | "work" | "study" | "business" | "tourism" | "digital_nomad"

export const ADMIN_GOAL_TYPE_OPTIONS: readonly GoalType[] = [
  "relocation",
  "work",
  "study",
  "business",
  "tourism",
  "digital_nomad",
]

/** Admin visa_metadata visa_type values. */
export type VisaType =
  | "visitor"
  | "work"
  | "student"
  | "business"
  | "tourist"
  | "relocation"
  | "digital_nomad"

/** Stored in `requirements.visa_status`. */
export type VisaStatus = "visa_free" | "visa_required" | "visa_on_arrival" | "e_visa" | "unknown"

export type VisaMetadataRequirements = {
  visa_status: VisaStatus | string
  max_stay?: number
  processing_time?: string
  cost_usd?: number
  note?: string
  /** Set only when a digital_nomad classification fell back to work. */
  is_digital_nomad_fallback?: boolean
  /** Official / commonly-used name of DN visa (when applicable). */
  digital_nomad_visa_name?: string
  /** Extra explanation note specific to digital nomad handling. */
  digital_nomad_note?: string
}

/** Normalized row for admin UI and forms (maps to/from `public.visa_metadata`). */
export type VisaMetadataRow = {
  id: string
  nationality_code: string
  destination_code: string
  /**
   * ISO 4217. Stored as `destination_currency` in DB; used as the traveler’s default budget
   * currency when seeding visa tasks (falls back before USD). Shown in admin as “nationality currency”.
   */
  destination_currency: string | null
  country: string
  goal_type: GoalType | string
  visa_type: VisaType | string
  required_documents: string[]
  requirements: VisaMetadataRequirements
  /** DB `enrichment_status`; null when unset in DB (shown as “none” in admin). */
  enrichment_status: string | null
  /** Primary language for enriched narrative content (`en` | `pt-BR`); null treated as `en` in UI. */
  content_locale: string | null
  /** Admin verification lifecycle (`unverified` | `pending_review` | `verified`). */
  verification_status: string | null
  /** Admin follow-up marker; independent of verification (live rows must stay verified). */
  flag_for_review: boolean
  /** Optional numeric confidence from DB/import; not written by {@link payloadToDb}. */
  confidence_score: number | null
  /** Model/provider tag from enrichment (e.g. gemini, mistral). */
  enrichment_source: string | null
  /** When the route was last enriched (ISO from DB). */
  enriched_at: string | null
  /** Admin notes that survive enrichment updates. */
  admin_notes: string | null
  /** Manual overrides applied over AI output when serving to users. */
  manual_override: VisaManualOverride | null
  /** When manual_override was last changed (ISO from DB). */
  override_updated_at: string | null
  /**
   * AI-generated visa process envelope (`visa_metadata.process_steps` JSON).
   * Omitted from {@link payloadToDb}; preserved on save unless changed elsewhere.
   */
  process_steps: unknown | null
  created_at: string
  updated_at: string
}

const GOAL_TYPES = ADMIN_GOAL_TYPE_OPTIONS

const VISA_TYPES: readonly VisaType[] = [
  "visitor",
  "work",
  "student",
  "business",
  "tourist",
  "relocation",
  "digital_nomad",
]

export const ADMIN_VISA_STATUS_OPTIONS: readonly VisaStatus[] = [
  "visa_free",
  "visa_required",
  "visa_on_arrival",
  "e_visa",
  "unknown",
]

const VISA_STATUSES = ADMIN_VISA_STATUS_OPTIONS

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v)
}

function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.filter((x): x is string => typeof x === "string")
}

function asVisaManualOverride(v: unknown): VisaManualOverride | null {
  if (v === null || v === undefined) return null
  if (!isRecord(v)) return null
  const o: VisaManualOverride = {}

  if (Array.isArray(v.steps_override)) {
    o.steps_override = v.steps_override as import("@/lib/visa-enrichment-normalize").VisaEnrichmentStep[]
  }
  if (typeof v.notes_override === "string") o.notes_override = v.notes_override
  if (typeof v.cost_override === "number" && Number.isFinite(v.cost_override)) o.cost_override = v.cost_override
  if (typeof v.timeline_override === "string") o.timeline_override = v.timeline_override
  if (Array.isArray(v.documents_override)) o.documents_override = asStringArray(v.documents_override)
  if (Array.isArray(v.tips_override)) o.tips_override = asStringArray(v.tips_override)
  if (Array.isArray(v.pitfalls_override)) o.pitfalls_override = asStringArray(v.pitfalls_override)

  return Object.keys(o).length > 0 ? o : null
}

/** Parse `visa_metadata.requirements` JSON (same rules as admin forms). */
export function parseRequirements(raw: unknown): VisaMetadataRequirements {
  if (!isRecord(raw)) {
    return { visa_status: "unknown" }
  }
  const visa_status =
    typeof raw.visa_status === "string" && raw.visa_status.length > 0 ? raw.visa_status : "unknown"
  let max_stay: number | undefined
  if (typeof raw.max_stay === "number" && Number.isFinite(raw.max_stay)) {
    max_stay = raw.max_stay
  } else if (typeof raw.max_stay === "string") {
    const n = Number.parseInt(raw.max_stay, 10)
    if (Number.isFinite(n)) max_stay = n
  }
  const processing_time =
    typeof raw.processing_time === "string" ? raw.processing_time : undefined
  let cost_usd: number | undefined
  if (typeof raw.cost_usd === "number" && Number.isFinite(raw.cost_usd)) {
    cost_usd = raw.cost_usd
  } else if (typeof raw.cost_usd === "string") {
    const n = Number.parseFloat(raw.cost_usd)
    if (Number.isFinite(n)) cost_usd = n
  }
  const note =
    typeof raw.note === "string"
      ? raw.note
      : typeof raw.notes === "string"
        ? raw.notes
        : undefined

  const is_digital_nomad_fallback = raw.is_digital_nomad_fallback === true ? true : undefined
  const digital_nomad_visa_name =
    typeof raw.digital_nomad_visa_name === "string" && raw.digital_nomad_visa_name.trim()
      ? raw.digital_nomad_visa_name.trim()
      : undefined
  const digital_nomad_note =
    typeof raw.digital_nomad_note === "string" && raw.digital_nomad_note.trim()
      ? raw.digital_nomad_note.trim()
      : undefined

  return {
    visa_status,
    max_stay,
    processing_time,
    cost_usd,
    note,
    ...(is_digital_nomad_fallback ? { is_digital_nomad_fallback } : {}),
    ...(digital_nomad_visa_name ? { digital_nomad_visa_name } : {}),
    ...(digital_nomad_note ? { digital_nomad_note } : {}),
  }
}

/** Serialize `requirements` for DB JSON (visa_metadata.requirements). */
export function requirementsToJson(req: VisaMetadataRequirements): Record<string, unknown> {
  const o: Record<string, unknown> = { ...req, visa_status: req.visa_status }
  if (req.max_stay !== undefined) o.max_stay = req.max_stay
  if (req.processing_time !== undefined) o.processing_time = req.processing_time
  if (req.cost_usd !== undefined) o.cost_usd = req.cost_usd
  if (req.note !== undefined && req.note !== "") o.note = req.note
  if (req.is_digital_nomad_fallback === true) o.is_digital_nomad_fallback = true
  if (typeof req.digital_nomad_visa_name === "string" && req.digital_nomad_visa_name.trim()) {
    o.digital_nomad_visa_name = req.digital_nomad_visa_name.trim()
  }
  if (typeof req.digital_nomad_note === "string" && req.digital_nomad_note.trim()) {
    o.digital_nomad_note = req.digital_nomad_note.trim()
  }
  return o
}

/** Map a Supabase row (unknown shape) to `VisaMetadataRow`; returns null if `id` missing. */
export function rowFromDb(raw: unknown): VisaMetadataRow | null {
  if (!isRecord(raw)) return null
  const id = typeof raw.id === "string" ? raw.id : null
  if (!id) return null

  const nationality_code =
    typeof raw.nationality_code === "string" ? raw.nationality_code.toUpperCase().slice(0, 2) : ""
  const destination_code =
    typeof raw.destination_code === "string" ? raw.destination_code.toUpperCase().slice(0, 2) : ""

  const dcRaw =
    typeof raw.destination_currency === "string" ? raw.destination_currency.trim().toUpperCase() : ""
  const destination_currency =
    dcRaw.length === 3 && /^[A-Z]{3}$/.test(dcRaw) ? dcRaw : null

  const country = typeof raw.country === "string" ? raw.country : ""

  const goalRaw = typeof raw.goal_type === "string" ? raw.goal_type : "tourism"
  const goal_type = (GOAL_TYPES as readonly string[]).includes(goalRaw)
    ? (goalRaw as GoalType)
    : goalRaw

  const visaRaw = typeof raw.visa_type === "string" ? raw.visa_type : "visitor"
  const visa_type = (VISA_TYPES as readonly string[]).includes(visaRaw)
    ? (visaRaw as VisaType)
    : visaRaw

  const required_documents = asStringArray(raw.required_documents)

  const requirements = parseRequirements(raw.requirements)

  const rawEs = raw.enrichment_status
  let enrichment_status: string | null
  if (rawEs === null || rawEs === undefined) {
    enrichment_status = null
  } else if (typeof rawEs === "string") {
    const trimmed = rawEs.trim()
    enrichment_status = trimmed.length > 0 ? trimmed : null
  } else {
    enrichment_status = null
  }

  const rawCl = raw.content_locale
  const content_locale: string | null =
    typeof rawCl === "string" && rawCl.trim() !== "" ? rawCl.trim() : "en"

  const rawVs = raw.verification_status
  let verification_status: string | null
  if (rawVs === null || rawVs === undefined) {
    verification_status = null
  } else if (typeof rawVs === "string") {
    const trimmed = rawVs.trim()
    verification_status = trimmed.length > 0 ? trimmed : null
  } else {
    verification_status = null
  }

  const rawFlag = raw.flag_for_review
  const flag_for_review = rawFlag === true

  let confidence_score: number | null = null
  const csRaw = raw.confidence_score
  if (typeof csRaw === "number" && Number.isFinite(csRaw)) {
    confidence_score = csRaw
  } else if (typeof csRaw === "string" && csRaw.trim() !== "") {
    const n = Number.parseFloat(csRaw)
    if (Number.isFinite(n)) confidence_score = n
  }

  const srcRaw = raw.enrichment_source
  const enrichment_source =
    typeof srcRaw === "string" && srcRaw.trim().length > 0 ? srcRaw.trim() : null

  const atRaw = raw.enriched_at
  let enriched_at: string | null = null
  if (typeof atRaw === "string" && atRaw.length > 0) {
    enriched_at = atRaw
  } else if (atRaw instanceof Date && !Number.isNaN(atRaw.getTime())) {
    enriched_at = atRaw.toISOString()
  }

  const created_at =
    typeof raw.created_at === "string" ? raw.created_at : new Date().toISOString()
  const updated_at =
    typeof raw.updated_at === "string" ? raw.updated_at : created_at

  const admin_notes = typeof raw.admin_notes === "string" ? raw.admin_notes : null

  let manual_override: VisaManualOverride | null = null
  const moRaw = raw.manual_override
  if (typeof moRaw === "string") {
    try {
      manual_override = asVisaManualOverride(JSON.parse(moRaw) as unknown)
    } catch {
      manual_override = null
    }
  } else {
    manual_override = asVisaManualOverride(moRaw)
  }

  const ouaRaw = raw.override_updated_at
  let override_updated_at: string | null = null
  if (typeof ouaRaw === "string" && ouaRaw.length > 0) {
    override_updated_at = ouaRaw
  } else if (ouaRaw instanceof Date && !Number.isNaN(ouaRaw.getTime())) {
    override_updated_at = ouaRaw.toISOString()
  }

  let process_steps: unknown | null = null
  const psRaw = raw.process_steps
  if (psRaw !== null && psRaw !== undefined) {
    if (typeof psRaw === "string") {
      try {
        process_steps = JSON.parse(psRaw) as unknown
      } catch {
        process_steps = null
      }
    } else {
      process_steps = psRaw
    }
  }

  return {
    id,
    nationality_code,
    destination_code,
    destination_currency,
    country,
    goal_type,
    visa_type,
    required_documents,
    requirements,
    enrichment_status,
    content_locale,
    verification_status,
    flag_for_review,
    confidence_score,
    enrichment_source,
    enriched_at,
    admin_notes,
    manual_override,
    override_updated_at,
    process_steps,
    created_at,
    updated_at,
  }
}

/** Payload for Supabase insert/update (JSON columns as plain objects/arrays). */
export function payloadToDb(row: VisaMetadataRow, mode: "insert" | "update"): Record<string, unknown> {
  const nat = row.nationality_code.trim().toUpperCase().slice(0, 2)
  const dest = row.destination_code.trim().toUpperCase().slice(0, 2)
  const country =
    row.country.trim() ||
    (dest.length === 2 ? `Destination ${dest}` : "Unknown")

  const goal =
    typeof row.goal_type === "string" && row.goal_type.length > 0 ? row.goal_type : "tourism"

  const visa =
    typeof row.visa_type === "string" && row.visa_type.length > 0 ? row.visa_type : "visitor"

  const required_documents = row.required_documents.filter((s) => s.trim().length > 0)
  const requirements = requirementsToJson(row.requirements)

  const dcIn = (row.destination_currency ?? "").trim().toUpperCase().replace(/[^A-Za-z]/g, "").slice(0, 3)
  const destination_currency =
    dcIn.length === 3 && /^[A-Z]{3}$/.test(dcIn) ? dcIn : null

  const base: Record<string, unknown> = {
    country,
    goal_type: goal,
    nationality_code: nat.length === 2 ? nat : null,
    destination_code: dest.length === 2 ? dest : null,
    destination_currency,
    visa_type: visa,
    required_documents,
    requirements,
    content_locale: row.content_locale ?? "en",
    admin_notes: row.admin_notes,
    manual_override: row.manual_override,
  }

  if (mode === "insert") {
    return { ...base, id: row.id }
  }

  return base
}

export function defaultEmptyVisaMetadataRow(): VisaMetadataRow {
  const now = new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    nationality_code: "",
    destination_code: "",
    destination_currency: null,
    country: "",
    goal_type: "tourism",
    visa_type: "visitor",
    required_documents: [],
    requirements: { visa_status: "unknown" },
    enrichment_status: "pending",
    content_locale: "en",
    verification_status: "unverified",
    flag_for_review: false,
    confidence_score: null,
    enrichment_source: null,
    enriched_at: null,
    admin_notes: null,
    manual_override: null,
    override_updated_at: null,
    process_steps: null,
    created_at: now,
    updated_at: now,
  }
}

export function isGoalType(v: string): v is GoalType {
  return (GOAL_TYPES as readonly string[]).includes(v)
}

export function isVisaType(v: string): v is VisaType {
  return (VISA_TYPES as readonly string[]).includes(v)
}

export function isVisaStatus(v: string): v is VisaStatus {
  return (VISA_STATUSES as readonly string[]).includes(v)
}
